import {useMemo} from 'react';
import {Condition, Mode} from '@legit.health/ui';
import {Autocomplete, TextField} from '@mui/material';
import {TFunction} from 'i18next';
import {
  FULL_BODY_SITE,
  NO_CONDITION
} from '@/shared/components/diagnosticReport/DiagnosticReportForm/const/noCondition';
import {useSharedDataContext} from '@/shared/components/layout/LoggedInLayout/contexts/SharedDataContext';
import {ConditionSelectItem} from '@/shared/components/patient/condition/ConditionSelector';
import FullBodySiteItem from './components/FullBodySiteItem';

export default function ConditionSelector({
  selectedConditionId,
  conditions,
  onSelectCondition,
  selectedPathologyId,
  t
}: {
  selectedPathologyId: string;
  selectedConditionId: string | null;
  conditions: Condition[];
  onSelectCondition: (conditionId: string | null) => void;
  t: TFunction;
}) {
  const {pathologiesById, scoringSystemsById} = useSharedDataContext();
  const pathology = pathologiesById[parseInt(selectedPathologyId)];
  if (!pathology) {
    throw new Error(`Pathology with id ${selectedPathologyId} not found`);
  }
  const scoringSystems = pathology.scoringSystemsIds
    .map((id) => scoringSystemsById[id])
    .filter(Boolean);
  const hasAutomaticGlobalScoringSystem = Boolean(
    scoringSystems.find((ss) => ss?.mode === Mode.AutomaticGlobal)
  );

  const options = useMemo(
    () => [
      ...(hasAutomaticGlobalScoringSystem
        ? [
            {
              id: FULL_BODY_SITE
            }
          ]
        : []),
      ...conditions.filter((condition) => `${condition.pathology.id}` === selectedPathologyId)
    ],
    [conditions, selectedPathologyId, hasAutomaticGlobalScoringSystem]
  );

  const selectedOption = options.find((option) => `${option.id}` === selectedConditionId);

  return (
    <Autocomplete
      options={options}
      autoHighlight
      onChange={(_, newValue) => onSelectCondition(newValue ? `${newValue.id}` : null)}
      value={selectedOption ?? null}
      getOptionLabel={(option) => {
        if (option.id === FULL_BODY_SITE) {
          return t('ui:diagnosticReport.fullBody');
        } else if (option.id === NO_CONDITION) {
          return t('shared/common:diagnosticReport.form.selectPathology.newPathology');
        } else {
          const condition = option as Condition;
          return t('shared/common:condition', {
            pathology: condition.pathology.name,
            bodySite: condition.bodySite.name.toLowerCase()
          });
        }
      }}
      getOptionKey={(option) => `${option.id}`}
      renderOption={(props, option) => {
        const {key, ...liProps} = props;
        if (option.id === FULL_BODY_SITE) {
          return <FullBodySiteItem key="-2" liProps={liProps} t={t} />;
        } else {
          return (
            <ConditionSelectItem key={key} condition={option as Condition} liProps={liProps} />
          );
        }
      }}
      renderInput={(params) => (
        <TextField {...params} label={t('shared/common:diagnosticReport.form.selectBodySite')} />
      )}
    />
  );
}
